import React, { useState, useEffect, useCallback, useMemo } from "react";
import styled, { withTheme } from "styled-components";
import { connect } from "react-redux";
import GoalsSectionTitle from "../../../ui-library/HeaderTitles/GoalsSectionTitle";
import EntriesCard from "../../../library/LCF401/EntriesCard/EntriesCard";
import dynamic from "@redux-dynostore/react-redux";
import subspaced from "@redux-dynostore/react-redux-subspace";
import { attachReducer } from "@redux-dynostore/redux-subspace";
import DietDiary from "../DietDiary/DietDiary";
import CustomModal, {
  ModalSize,
} from "../../../ui-library/CustomModal/CustomModal";
import DaySummary from "../../../library/LCF401/DaySummary/DaySummary";
import moment from "moment";
import {
  dailyEntries,
  mapDispatchToProps,
  mapStateToProps,
} from "./DailyEntries.store";
import { ToastType } from "../../../ToastManager/constants";
import { sendNotification } from "../../../ToastManager/ToastManager";
import {
  DietDairyTypes,
  dietDairyTypeToString,
} from "../DietDiary/DietDiary.constants";
import { useTranslation } from "react-i18next";
import {
  decimalPlaces,
  isFastingDay,
  getRoundedValue,
  roundMealValue,
  emptyTotals,
  areTotalsEmpty,
  fastingDayEmptyMeal,
} from "../../../../helpers/mealsHelper";
import MacronutrientsTotal from "../../../library/MacronutrientsTotal/MacronutrientsTotal";
import { DateFormat } from "../../../../modules/constants";

const EmptyDataBoard = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PanelsWrapper = styled.div`
  margin-left: 0px;
  margin-right: 0px;
  width: 100%;

  & > div {
    margin-right: 15px;
    margin-bottom: 15px;
    flex: 0.45;
  }
  & > #entries-list {
    min-width: 50%;
    flex: 1;
    margin-left: 20px;
    > div {
      box-shadow: none;
      height: 100%;
    }
  }

  @media (max-width: 1279px) {
    flex-direction: column;
    & > #entries-list {
      min-width: unset;
      width: inherit;
      margin-left: 0;
    }
    & > div {
      margin-right: 0;
    }
  }
`;

const TotalsWrapper = styled.div`
  background: white;
  border-radius: 12px;
  padding: 20px;
  display: flex;
  flex-direction: column;
`;

const TotalsTitle = styled.label`
  font-size: 19px;
  font-weight: 600;
  color: ${(props) => props.theme?.nero};
  margin-bottom: 14px;
`;

export const DailyEntries = (props) => {
  const {
    date,
    title,
    totalsTitle,
    listTitle,
    listActionTitle,
    closeDietDiaryAction,
    fetchEntries,
    completeDay,
    loadingEntries,
    loadingEntriesError,
    entries,
    studyId,
    canCompleteDay,
    canViewMacronutrients,
    canEditMeal,
    refetchData,
    saveFastingDayMeal,
    theme,
    participantDateOfEnrollment,
  } = props;

  const { t } = useTranslation();

  const minDateMeal = useMemo(() => {
    if (
      participantDateOfEnrollment &&
      moment(participantDateOfEnrollment).startOf("day") <
        moment().startOf("day").subtract(2, "days")
    ) {
      return moment().startOf("day").subtract(2, "days").toDate();
    }

    return moment(participantDateOfEnrollment).startOf("day").toDate();
  }, [participantDateOfEnrollment]);

  const isFastingDayItem = useMemo(() => isFastingDay(entries), [entries]);

  const anyFastingDayItems = useMemo(
    () =>
      entries?.meals?.filter((item) => {
        return item.fastingDay;
      }),
    [entries]
  );

  const isDayCompleted = useMemo(
    () => entries?.meals[0]?.completed || anyFastingDayItems?.length > 0,
    [entries, anyFastingDayItems]
  );

  const isAbleToCompleteDay = useMemo(() => {
    return isDayCompleted || minDateMeal > date.startOf("day").toDate();
  }, [isDayCompleted, minDateMeal, date]);

  const [completeDayData, setCompleteDayData] = useState(null);

  const [selectedDietDiaryOption, setSelectedDietDiaryOption] = useState(null);
  const [dietDiaryModalTitle, setDietDiaryModalTitle] = useState("");

  const [totalsData, setTotalsData] = useState(emptyTotals);
  const [formatedEntries, setFormatedEntries] = useState(null);

  const formatEntries = useCallback(
    (entries) => {
      const finalArray = [];
      if (entries) {
        entries.meals.forEach((elem) => {
          const mealsItems = [];
          let jsonData = elem?.mealJson;
          jsonData =
            typeof jsonData === "string" ? JSON.parse(elem.mealJson) : jsonData;

          let rowTitle = "";
          let rowDate = null;
          if (jsonData) {
            jsonData.items.forEach((jsonElem) => {
              rowTitle += `${jsonElem.title}, `;
              rowDate = elem.mealDate;
              mealsItems.push({
                title: jsonElem.title,
                date: elem.mealDate,
                dayDate: date,
                elem: elem,
              });
            });
          }

          // removing last comma
          rowTitle = rowTitle.slice(0, -2);
          if (mealsItems.length > 0) {
            finalArray.push({
              title: rowTitle,
              key: elem.key,
              completed: elem.completed,
              isFastingDay: isFastingDay(mealsItems),
              json: elem.mealJson,
              date: rowDate,
              reason: elem.reason,
              items: mealsItems,
              is24hRecall: elem.recall,
              hceMceEvent: elem.hceMceEvent,
            });
          }
        });
      }

      finalArray.sort((elemPrev, elemNext) =>
        elemNext.date.localeCompare(elemPrev.date)
      );
      return finalArray;
    },
    [date]
  );

  useEffect(() => {
    if (entries && entries.meals && entries.meals.length) {
      const formatedEntriesData = formatEntries(entries);
      setFormatedEntries(formatedEntriesData);
    } else {
      setFormatedEntries([]);
    }
  }, [formatEntries, entries]);

  useEffect(() => {
    if (formatedEntries) {
      const totalCaloriesCount = formatedEntries.reduce((total, entry) => {
        const jsonData = entry.json ? JSON.parse(entry.json) : null;
        return getRoundedValue(
          Number(total ?? 0) + Number(jsonData?.aboutMeal?.Calories?.value ?? 0)
        );
      }, 0);

      const totalMediumChainFatsCount = formatedEntries.reduce(
        (total, entry) => {
          const jsonData = entry.json ? JSON.parse(entry.json) : null;
          return getRoundedValue(
            Number(total ?? 0) +
              Number(jsonData?.aboutMeal?.MediumChainFats?.value ?? 0)
          );
        },
        0
      );

      const totalLongChainFatsCount = formatedEntries.reduce((total, entry) => {
        const jsonData = entry.json ? JSON.parse(entry.json) : null;
        return total + (jsonData?.aboutMeal?.LongChainFats?.value || 0);
      }, 0);

      const totalFatCount = formatedEntries.reduce((total, entry) => {
        const jsonData = entry.json ? JSON.parse(entry.json) : null;
        return getRoundedValue(
          Number(total ?? 0) + Number(jsonData?.aboutMeal?.Fats?.value ?? 0)
        );
      }, 0);

      setTotalsData({
        calories: `${roundMealValue(totalCaloriesCount).toFixed(
          decimalPlaces
        )} kcal`,
        totalFats: `${roundMealValue(totalFatCount).toFixed(decimalPlaces)} g`,
        mediumChainFats: `${roundMealValue(totalMediumChainFatsCount).toFixed(
          decimalPlaces
        )} g`,
        longChainFats: `${roundMealValue(totalLongChainFatsCount).toFixed(
          decimalPlaces
        )} g`,
      });
    }
  }, [formatedEntries, date, props.theme, theme, studyId, t]);

  const finishDayConfirm = () => {
    setCompleteDayData({
      day: date,
      totals: totalsData,
    });
  };

  const markDayAsFasting = () => {
    // Marking day as fasted
    // Adding an empty meal
    const dateFormated = moment(date.format(DateFormat.INVERTED_DATE));
    const timeValue = date.format("HH:mm").split(":");
    dateFormated.set({
      h: timeValue[0],
      m: timeValue[1],
    });

    const formData = {
      mealDate: dateFormated.format(DateFormat.EXTENDED_DATETIME),
      mealJson: fastingDayEmptyMeal,
      reasonForChange: t("appText.fastingDayLabelReason"),
    };
    saveFastingDayMeal(formData, DietDairyTypes.FOOD_DRINK)
      .then(() => {
        finishDay();
      })
      .catch((error) => {
        sendNotification(error, ToastType.ERROR);
      });
  };

  const finishDay = () => {
    setCompleteDayData(null);
    completeDay({
      day: moment(date).format(DateFormat.INVERTED_DATE),
      fastingDay: areTotalsEmpty(totalsData),
    })
      .then((message) => {
        refetchData(date);
        sendNotification(message, ToastType.SUCCESS);
      })
      .catch((e) => sendNotification(e, ToastType.ERROR));
  };

  const entryEdit = (element) => {
    const type = JSON.parse(element.json)?.type ?? DietDairyTypes.FOOD_DRINK;

    setDietDiaryModalTitle(
      `${t("appText.edit")} ${dietDairyTypeToString(type)}`
    );

    setSelectedDietDiaryOption({
      mealId: element.key,
      jsonData: element.json,
      date: element.date,
      type: type,
      readOnly: element.readOnly,
      reason: element.reason,
      reasonForChange: element.reasonForChange,
      is24hRecall: element.is24hRecall,
      hceMceEvent: element.hceMceEvent,
    });
  };

  const onCloseDietDiaryModal = () => {
    setSelectedDietDiaryOption(null);
    if (closeDietDiaryAction) {
      closeDietDiaryAction();
    } else {
      fetchEntries(date);
    }
  };

  const onCloseCompleteDayModal = () => {
    setCompleteDayData(null);
  };

  return (
    <div data-testid="daily-entries">
      <GoalsSectionTitle
        label={title}
        date={`${date.format(DateFormat.EXTENDED_FORMAT)}`}
      />
      <PanelsWrapper className="row">
        <EntriesCard
          id="entries-list"
          title={
            listTitle || t("contentText.dietDiary.entriesPanel.today.title")
          }
          elements={formatedEntries}
          showActionButton={canCompleteDay}
          actionButtonText={
            listActionTitle || t("appText.dietDiary.completeDay")
          }
          actionButton={finishDayConfirm}
          isFastingDay={isFastingDayItem}
          actionButtonInactive={isAbleToCompleteDay}
          loadingEntries={loadingEntries}
          loadingEntriesError={loadingEntriesError}
          onEdit={(elementEdit) => entryEdit(elementEdit)}
          canEdit={canEditMeal && !isDayCompleted}
        />

        {canViewMacronutrients && (
          <TotalsWrapper>
            <TotalsTitle>{totalsTitle}</TotalsTitle>
            {totalsData ? (
              <MacronutrientsTotal {...totalsData} />
            ) : (
              <EmptyDataBoard>
                <p>{t("appText.noData")}</p>
              </EmptyDataBoard>
            )}
          </TotalsWrapper>
        )}
      </PanelsWrapper>

      <CustomModal
        show={selectedDietDiaryOption !== null}
        title={dietDiaryModalTitle ?? ""}
        size={ModalSize.large}
        onClose={() => onCloseDietDiaryModal()}
      >
        <DietDiary
          mealJsonData={selectedDietDiaryOption?.jsonData}
          mealId={selectedDietDiaryOption?.mealId}
          onSubmit={() => onCloseDietDiaryModal()}
          onClose={() => onCloseDietDiaryModal()}
          maxDate={moment().toDate()}
          minDate={minDateMeal}
          isFastingDay={isFastingDayItem}
          mealDate={selectedDietDiaryOption?.date}
          readOnly={selectedDietDiaryOption?.readOnly}
          dietDiaryOption={selectedDietDiaryOption}
          setModalTitle={setDietDiaryModalTitle}
          existingReason={selectedDietDiaryOption?.reason}
          existingReasonForChange={selectedDietDiaryOption?.reasonForChange}
          type={selectedDietDiaryOption?.type}
          studyId={studyId}
          mealIs24hRecall={selectedDietDiaryOption?.is24hRecall || false}
          mealHceMceEvent={selectedDietDiaryOption?.hceMceEvent || false}
        />
      </CustomModal>

      {completeDayData !== null && totalsData !== null && (
        <CustomModal
          show={completeDayData !== null && totalsData !== null}
          title={t("contentText.dietDiary.completeDay.title")}
          size={ModalSize.small}
          onClose={() => onCloseCompleteDayModal()}
        >
          <DaySummary
            isFastingDayConfirmation={areTotalsEmpty(totalsData)}
            isModal={true}
            completeDayData={completeDayData}
            totalsData={totalsData}
            finishDay={() =>
              !isDayCompleted && areTotalsEmpty(totalsData)
                ? markDayAsFasting()
                : finishDay()
            }
            closeModal={() => onCloseCompleteDayModal()}
            studyId={studyId}
          />
        </CustomModal>
      )}
    </div>
  );
};

export default withTheme(
  dynamic(
    "dailyEntries",
    subspaced({
      // We map extra state because we might need to acess other stores for data, subspace is self contained.
      mapExtraState: (state) => ({
        loginReducer: state.loginReducer,
      }),
    }),
    attachReducer(dailyEntries)
  )(connect(mapStateToProps, mapDispatchToProps)(DailyEntries))
);
